import React, { memo, useCallback, useMemo, useState } from 'react'
import { Alert, Modal, Spin } from 'antd'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import schema from './schema.json'
import { convertData2JsonForm } from '../../../../../shared/lib/convertData2JsonForm'
import { isFormDataRequired } from '../../../../../../lib/utils/collections'

interface IFormData {
  delivery_date?: number
  region_id?: number
}

interface MeasurementModalProps {
  isModalOpen: boolean
  onOk?: ((formData: IFormData) => void) | undefined
  onCancel?: ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined
}

export const UnloadFromWDModal: React.FC<MeasurementModalProps> = memo(
  (props: MeasurementModalProps) => {
    const { isModalOpen, onOk, onCancel } = props

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [formData, setFormData] = useState<IFormData>({
      delivery_date: undefined,
      region_id: undefined,
    })

    const handleOk = useCallback(async () => {
      const isRequiredFields = isFormDataRequired(formData, actualSchema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }
      try {
        setLoading(true)
        await onOk?.(formData)
      } catch (err) {
        setError('Произошла ошибка. Попробуйте снова.')
      } finally {
        setLoading(false)
      }
    }, [formData, onOk])

    const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
      setError(null)
      onCancel?.(e)
    }

    const onChange = useCallback(
      data => {
        setFormData(prevData => {
          return {
            ...prevData,
            ...data.formData,
          }
        })
      },
      [setFormData],
    )

    interface ISchemaActual {
      resultObject: {}
      resultSchema: {
        properties: {
          delivery_date: any
          region_id: any
        }
        required: string[]
      }
    }

    const actualSchema = useMemo(() => {
      const schemaActual = (convertData2JsonForm(schema.fields) as never) as ISchemaActual

      return schemaActual.resultSchema || {}
    }, [schema, formData])

    if (!isModalOpen) {
      return null
    }

    return (
      <Modal
        title={schema.title}
        onOk={handleOk}
        onCancel={handleCancel}
        open={isModalOpen}
        destroyOnHidden={true}
        okButtonProps={{
          disabled: loading,
        }}
      >
        {loading ? (
          <Spin />
        ) : error ? (
          <Alert message={error} type="error" showIcon />
        ) : (
          <DefaultJsonForm
            formData={formData}
            schema={actualSchema}
            onChange={onChange}
            orientation="horizontal"
          />
        )}
      </Modal>
    )
  },
)
